import { ProductSpecification } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { ProductDetailsContext, ProductSection } from '@components';
import Masonry from '@mui/lab/Masonry';
import { Box, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { useContext, useMemo } from 'react';

// STYLES
//--------

const MasonryBox = styled(Box)`
  position: relative;
  ${breakpoints.down('lg')} {
    margin: 0 -16px;
    & .MuiMasonry-root {
      margin: auto !important;
    }
  }
`;
const Line = styled(Box)`
  ${breakpoints.up('lg')} {
    background: #2a495980;
    position: absolute;
    top: 0;
    left: 50%;
    bottom: 0;
    width: 1px;
  }
`;
const SpecsMasonry = styled(Masonry)`
  width: auto !important;
`;

export const ProductSpecifications = () => {
  const { product = {}, sections, onSectionEnter } = useContext(ProductDetailsContext);
  const {
    itemPackageBatteryCharger = {},
    itemPackageNumberBatteries = {},
    itemPackageNumberChargers = {},
  } = product;

  const batteries = itemPackageNumberBatteries?.value;
  const chargers = itemPackageNumberChargers?.value;
  const charger = itemPackageBatteryCharger?.value?.[1];
  const battery = itemPackageBatteryCharger?.value?.[0];

  return sections?.specifications ? (
    <ProductSection
      sectionId='specifications'
      title={t(TextCodes.Product.Details.Specifications)}
      onSectionEnter={onSectionEnter}
    >
      <MasonryBox>
        <SpecsMasonry
          spacing={{
            xs: 0,
            lg: 8,
          }}
          columns={{ xs: 1, lg: 2 }}
        >
          {Object.entries(product?.specifications || {}).map(([title, entry], key) => (
            <ProductSpecificationsSection
              key={key}
              title={title ? title.charAt(0).toUpperCase() + title.slice(1) : ''}
              items={entry}
            />
          ))}
          <ProductSpecificationsSection
            title={t(TextCodes.Product.Details.PackageDetails)}
            items={[
              { label: t(TextCodes.Product.Details.NumberOfBatteries), value: batteries },
              { label: t(TextCodes.Product.Details.Battery), value: battery },
              {
                label: t(TextCodes.Product.Details.NumberOfChargers),
                value: chargers,
              },
              { label: t(TextCodes.Product.Details.Charger), value: charger },
            ]}
          />
        </SpecsMasonry>
        <Line />
      </MasonryBox>
    </ProductSection>
  ) : null;
};

const SectionBox = styled(Box)`
  ${breakpoints.down('lg')} {
    margin-bottom: 24px !important;
  }
`;
const SectionTitle = styled(Box)`
  font-size: 16px;
  letter-spacing: 0.01em;
  font-weight: 700;
  line-height: 140%;
  letter-spacing: 0.01em;
  color: #3a454a;
  ${breakpoints.up('lg')} {
    margin-bottom: 16px;
  }
`;

const SpecItemBox = styled(Box)`
  border-bottom: 1px solid #2a495980;
  padding: 8px 0;
  display: flex;
  justify-content: space-between;
`;
const LabelBox = styled(Box)`
  color: ${colors.text.tertiary};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 160%;
  letter-spacing: 0.01em;
  margin-right: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ValueBox = styled(Box)`
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 160%;
  color: #1c1c1c;
  text-align: right;
`;

export type ProductSpecsSectionProps = {
  title?: string;
  items?: ProductSpecification[];
};

export const ProductSpecificationsSection = ({ title, items }: ProductSpecsSectionProps) => {
  const hasAnyValue = useMemo(() => items?.find((x) => x.value), [items]);
  if (!hasAnyValue) {
    return null;
  }
  return (
    <SectionBox>
      <SectionTitle>{title}</SectionTitle>
      <Box>
        {items?.map(({ label, value, unit }, key) =>
          value ? (
            <SpecItemBox key={key}>
              <LabelBox component='span'>{label}</LabelBox>
              <ValueBox component='span'>
                {Array.isArray(value) ? value.join(', ') : value} {unit}
              </ValueBox>
            </SpecItemBox>
          ) : null
        )}
      </Box>
    </SectionBox>
  );
};
